import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { TrashIcon } from "@/icons/TrashIcon";
import { AlertTriangleIcon } from "@/icons/AlertTriangleIcon";
import { InfoIcon } from "@/icons/InfoIcon";
import { ValidationError } from "./errors";
import styles from "./ShiftForm.module.css";
import type { ClashInfo, ShiftStatus } from "@/domain/shifts/types";

interface Cleaner {
  readonly membershipId: string;
  readonly name: string;
}

interface ExistingShift {
  readonly membershipId: string;
  readonly startTime: string;
  readonly endTime: string;
  readonly siteId: string;
}

interface ShiftFormProps {
  readonly anchorRef: React.RefObject<HTMLElement | null>;
  readonly siteId: string;
  readonly date: string;
  readonly cleaners: readonly Cleaner[];
  readonly existingShifts: readonly ExistingShift[];
  readonly shift?: {
    readonly id: string;
    readonly membershipId: string;
    readonly startTime: string;
    readonly endTime: string;
    readonly status: ShiftStatus;
  };
  readonly onSave: (data: {
    siteId: string;
    membershipId: string;
    date: string;
    startTime: string;
    endTime: string;
  }) => Promise<{ clashes?: readonly ClashInfo[] }>;
  readonly onUpdate?: (shiftId: string, data: {
    membershipId: string;
    startTime: string;
    endTime: string;
  }) => Promise<{ clashes?: readonly ClashInfo[] }>;
  readonly onDelete?: (shiftId: string) => Promise<void>;
  readonly onClose: () => void;
}

function timesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return startA < endB && startB < endA;
}

export function ShiftForm({
  anchorRef,
  siteId,
  date,
  cleaners,
  existingShifts,
  shift,
  onSave,
  onUpdate,
  onDelete,
  onClose,
}: ShiftFormProps) {
  const isEdit = !!shift;
  const formRef = useRef<HTMLDivElement>(null);
  const [membershipId, setMembershipId] = useState(shift?.membershipId ?? (cleaners[0]?.membershipId ?? ""));
  const [startTime, setStartTime] = useState(shift?.startTime ?? "09:00");
  const [endTime, setEndTime] = useState(shift?.endTime ?? "11:00");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const cleanerSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const formWidth = 224;
    const formHeight = 200;
    let top = rect.bottom + 4;
    let left = rect.left;

    if (top + formHeight > window.innerHeight) {
      top = rect.top - formHeight - 4;
    }
    if (left + formWidth > window.innerWidth) {
      left = window.innerWidth - formWidth - 8;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;

    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    if (pos) {
      cleanerSelectRef.current?.focus();
    }
  }, [pos]);

  const clientClashes = useMemo(() => {
    if (!membershipId || !startTime || !endTime || endTime <= startTime) return [];
    return existingShifts.filter(
      (s) =>
        s.membershipId === membershipId &&
        timesOverlap(startTime, endTime, s.startTime, s.endTime),
    );
  }, [membershipId, startTime, endTime, existingShifts]);

  const clashCleanerName = useMemo(() => {
    if (clientClashes.length === 0) return "";
    return cleaners.find((c) => c.membershipId === membershipId)?.name.split(" ")[0] ?? "This cleaner";
  }, [clientClashes, cleaners, membershipId]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setErrors({});

    try {
      if (isEdit && onUpdate && shift) {
        await onUpdate(shift.id, { membershipId, startTime, endTime });
      } else {
        await onSave({ siteId, membershipId, date, startTime, endTime });
      }
      onClose();
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(error.fields);
        setSaving(false);
        return;
      }
    } finally {
      setSaving(false);
    }
  }, [saving, isEdit, shift, onUpdate, onSave, onClose, siteId, membershipId, date, startTime, endTime]);

  const handleDelete = useCallback(async () => {
    if (!shift || !onDelete) return;
    setSaving(true);
    try {
      await onDelete(shift.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [shift, onDelete, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  }, [handleSave, onClose]);

  if (!pos) return null;

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div
        ref={formRef}
        className={styles.form}
        style={{ top: pos.top, left: pos.left }}
        onKeyDown={handleKeyDown}
      >
        <select
          ref={cleanerSelectRef}
          className={styles.cleanerSelect}
          aria-label="Cleaner"
          value={membershipId}
          onChange={(e) => setMembershipId(e.target.value)}
        >
          {cleaners.length === 0 && <option value="">No cleaners</option>}
          {cleaners.map((c) => (
            <option key={c.membershipId} value={c.membershipId}>{c.name}</option>
          ))}
        </select>

        <div className={styles.timeRow}>
          <div className={styles.timeGroup}>
            <label htmlFor="shift-start" className={styles.timeLabel}>Start</label>
            <input
              type="time"
              id="shift-start"
              aria-label="Start"
              className={styles.timeInput}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className={styles.timeGroup}>
            <label htmlFor="shift-end" className={styles.timeLabel}>End</label>
            <input
              type="time"
              id="shift-end"
              aria-label="End"
              className={styles.timeInput}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        {clientClashes.length > 0 && (
          <div className={styles.clashWarning}>
            <AlertTriangleIcon width={12} height={12} className={styles.clashIcon} />
            <span>{clashCleanerName} already has a shift from {clientClashes[0].startTime} to {clientClashes[0].endTime}</span>
          </div>
        )}

        {shift?.status === "published" && (
          <div className={styles.liveBanner} role="status">
            <InfoIcon width={12} height={12} className={styles.liveIcon} />
            <span>Live. Changes save straight to your cleaner's schedule.</span>
          </div>
        )}

        {errors.endTime && <div className={styles.error}>{errors.endTime}</div>}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
            aria-label="Save"
          >
            {saving ? "..." : "Save"}
          </button>
          {isEdit && (
            <button
              type="button"
              className={styles.deleteButton}
              onClick={handleDelete}
              disabled={saving}
              aria-label="Delete"
            >
              <TrashIcon width={14} height={14} />
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

